import React from 'react';
import Copyrights from '../Copyrights';
import Footer from '../Footer';
import SEO from '../SEO';
import '../Portfolio.css';

const SKILL_GROUPS = [
  {
    title: 'Cloud Platforms',
    skills: ['AWS (Redshift, Lambda, S3, EC2, Kinesis, Glue, SageMaker)', 'GCP (BigQuery, Dataproc, Pub/Sub, Dataflow, Vertex AI, AutoML)', 'Azure (Data Factory, Databricks, Synapse, OpenAI, DevOps)'],
  },
  {
    title: 'Data Engineering',
    skills: ['Apache Spark / PySpark', 'Apache Beam · Airflow', 'DBT · Snowflake', 'Hadoop · Hive · Kafka', 'Talend · ETL/ELT'],
  },
  {
    title: 'ML, Deep Learning & LLMs',
    skills: ['TensorFlow · Keras · PyTorch', 'Scikit-learn · OpenCV', 'LangChain · LangGraph', 'Hugging Face Transformers · spaCy', 'RAG pipelines · Prompt engineering', 'Vector DBs (FAISS, Chroma, Pinecone)'],
  },
  {
    title: 'Databases',
    skills: ['PostgreSQL · Oracle · MySQL', 'SQL Server · Aurora · BigQuery', 'MongoDB · InfluxDB · Aerospike · BigTable'],
  },
  {
    title: 'Observability & DevOps',
    skills: ['Docker · Git · CI/CD', 'Ansible / Ansible Tower', 'DataDog · MonteCarlo · Great Expectations', 'Azure DevOps · Jenkins · JIRA'],
  },
  {
    title: 'Governance & Methodology',
    skills: ['Data quality frameworks', 'LGPD/GDPR alignment', 'TDD · Agile / Scrum · Kanban', 'PMP · ITIL · COBIT · CMMI'],
  },
];

const TIMELINE = [
  {
    role: 'Senior Data Engineer',
    org: 'Thoughtworks',
    period: 'Jun 2025 – Jun 2026',
    location: 'São Paulo, Brazil',
    bullets: [
      'Architected GCP-native data pipelines using App Engine, Pub/Sub, Compute Engine, Apache Beam, and Airflow (Cloud Composer).',
      'Designed and maintained Dataflow streaming and batch jobs processing millions of daily events.',
      'Built ingestion, transformation, and serving layers in Python and Node.js, holding SLA and data-quality compliance.',
    ],
  },
  {
    role: 'Lead Data Engineer & Scientist',
    org: 'BairesDev',
    period: 'May 2024 – Jun 2025',
    location: 'Remote (US clients)',
    bullets: [
      'Multi-client engagement BairesDev.',
      'Architected end-to-end cloud-native pipelines on GCP (BigQuery, Dataproc, Pub/Sub, Beam, Dataflow, Airflow) and AWS (Redshift, Lambda, S3, PySpark).',
      'Implemented data observability with DataDog and Monte Carlo, using ML-based anomaly detection to enforce data-quality SLAs.',
      'Delivered Snowflake + dbt warehouse solutions serving real-time analytics and BI consumers.',
      'Collaborated on ML model deployments (AutoML, Vertex AI, Google Vision) and LLM integrations; built NLP apps to structure, summarize, and categorize text data.',
    ],
  },
  {
    role: 'Senior Data Engineer',
    org: 'OmeleteCompany',
    period: 'Aug 2024 – Mar 2026',
    location: 'São Paulo, Brazil',
    bullets: [
      'Built GCP-native and Databricks data pipelines (App Engine, Pub/Sub, Compute Engine, Beam, Airflow).',
      'Developed AI models (LLM and CNN) to extract metadata, structure, summarize, and categorize text for business classification and prediction.',
    ],
  },
  {
    role: 'Senior Data Engineer & Scientist',
    org: 'LOTS Group',
    period: 'Oct 2023 – Feb 2024',
    location: 'São Paulo, Brazil',
    bullets: [
      'Led data pipeline development on Microsoft Azure (Data Factory, Databricks, Synapse, DevOps).',
      'Built Power BI dashboards for senior stakeholders; introduced code review and TDD practices.',
      'Implemented incremental ETL patterns and data-quality monitoring, cutting data-refresh latency by 40%.',
    ],
  },
  {
    role: 'Senior Data Engineer',
    org: 'WEX Inc. (US Bank)',
    period: 'Aug 2022 – May 2023',
    location: 'Salvador, Brazil / USA Remote',
    bullets: [
      'Built Snowflake + DBT + AWS Redshift pipelines using Scala, PySpark, and Talend.',
      'Enforced data governance and compliance standards aligned with financial regulations.',
      'Automated data-quality checks with Great Expectations, reducing critical data incidents by 30%.',
    ],
  },
  {
    role: 'Data Engineer & Scientist (Research)',
    org: 'SENAI CIMATEC',
    period: 'Nov 2022 – Oct 2023',
    location: 'Salvador, Brazil',
    bullets: [
      'Designed ML/DL pipelines (TensorFlow, Keras, PyTorch, Scikit-learn, OpenCV) for computer vision and signal classification research.',
      'Built NLP pipelines (Hugging Face Transformers, spaCy) and explored LangChain for LLM-assisted research workflows.',
      'CNN/MLP models achieving 90%+ classification accuracy; mentored junior researchers.',
    ],
  },
  {
    role: 'Senior Data Engineer (DevOps)',
    org: 'BRQ',
    period: 'Dec 2021 – Aug 2022',
    location: 'São Paulo, Brazil / USA',
    bullets: [
      'Contributed to CI/CD pipeline design using Ansible, Ansible Tower, .NET, MS SQL Server, and GCP.',
      'Delivered automated reporting feeds replacing manual processes for risk and compliance teams.',
    ],
  },
  {
    role: 'Senior Data Engineer',
    org: 'Programmers',
    period: 'Jan 2020 – Dec 2021',
    location: 'São Paulo, Brazil',
    bullets: [
      'Built an Azure-based data platform: Data Lake, Azure OpenAI, Blob Storage, Synapse SQL Pools, Data Factory, Databricks.',
      'Designed ETL pipelines processing millions of daily records across finance, logistics, and commercial domains; delivered Power BI dashboards.',
    ],
  },
  {
    role: 'Founder & CTO / Lead Data Engineer',
    org: 'QONTEO Biometrics',
    period: 'Mar 2020 – Dec 2021',
    location: 'Napa, CA (Remote)',
    bullets: [
      'Founded and led the full technology stack for a B2B AI/biometrics platform, scaling to enterprise clients.',
      'Architected distributed systems on AWS (Rekognition, Kinesis, EC2, Lambda) and GCP (BigQuery, BigTable, AutoML, Vertex AI).',
      'Built the ML pipeline end to end, from ingestion (Aerospike, InfluxDB, MongoDB) to inference, serving 100K+ daily requests.',
      'Led a team of 4 engineers; introduced Scrum/Kanban delivery and mentoring programs.',
    ],
  },
  {
    role: 'Founder & Principal Consultant',
    org: 'IURIX (own consultancy)',
    period: 'Jan 2017 – Present',
    location: 'Salvador / São Paulo, Brazil',
    bullets: [
      'Includes a CTO & Technical Lead engagement at EvEx Tecnologia + Tango Tech (Jun 2017 – Feb 2020), leading ERP platform teams and BI pipelines on Azure.',
      'Designed data pipeline architectures scaling from 0 to 100K daily active users, ingesting from SQL, REST APIs, Salesforce, and GCP.',
      'Built ML models for sales forecasting, lifting online sales up to 10% per product.',
      'Designed LangChain/LangGraph agentic pipelines for document intelligence, multi-step reasoning, and RAG.',
      'Delivered full-stack projects (Python, JavaScript, React, React Native) for multinational clients — including this site.',
    ],
  },
  {
    role: 'Senior Software & Data Engineer / IT Business Analyst',
    org: 'GOL Linhas Aéreas',
    period: 'Mar 2013 – Mar 2017',
    location: 'São Paulo, Brazil',
    bullets: [
      'Led delivery of New Skies passenger control systems (Skyport, Skyspeed, Sky Utilities) — check-in, boarding, baggage, aircraft ops.',
      'Managed a 2-year Oracle-to-SAP ERP migration (financial, fiscal, inventory, procurement) for a national airline with 40M+ annual passengers, with zero critical downtime.',
    ],
  },
  {
    role: 'IT Consultant & Developer',
    org: 'United Nations (PNUD) + OAS',
    period: 'Aug 2004 – Aug 2011',
    location: 'Brasília, Brazil + Washington, DC',
    bullets: [
      'Delivered CRM and ITSM platforms (Python, TCL-TK, Oracle, PostgreSQL) for UN and OAS IT & marketing project governance.',
      'Developed CMS, e-commerce, LMS, and project management systems; administered Linux network infrastructure.',
    ],
  },
];

const EDUCATION = [
  { degree: 'MSc, Computational Modeling & Industrial Technology', school: 'SENAI CIMATEC, Salvador, Brazil', period: '2022 – 2025' },
  { degree: 'MSc, Automation, Robotics & Controlling for Industrial Technology', school: 'SENAI CIMATEC, Salvador, Brazil', period: '2020 – 2022' },
  { degree: 'MBA, Engineering & Management of Products and Services', school: 'Universidade de São Paulo (USP)', period: '2015 – 2016' },
  { degree: 'BSc, Computer Science', school: 'Universidade Federal da Bahia (UFBA)', period: '2002 – 2009' },
  { degree: 'Associate, Linux Systems Administration & TCP/IP Networks', school: 'USDA Graduate School, Washington DC', period: '2005' },
  { degree: 'Associate, Communication Skills', school: 'USDA Graduate School, Washington DC', period: '2005' },
];

const CERTIFICATIONS = [
  'PMP', 'ITIL', 'COBIT', 'Scrum (certified)', 'IEEE Member (since 2003)',
  'IEEE Computer Society (since 2004)', 'OpenACS Users Group (since 2005)', 'SAP Community Network (since 2009)',
];

const LANGUAGES = [
  { name: 'Portuguese', level: 'Native', pct: 100 },
  { name: 'English', level: 'Full Professional (C1+)', pct: 90 },
  { name: 'Spanish', level: 'Full Professional', pct: 90 },
  { name: 'French', level: 'Intermediate', pct: 55 },
];

const personSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Iuri de Araujo Sampaio',
  jobTitle: 'Lead Data Engineer & AI/ML Architect',
  email: 'mailto:contact@iurix.com',
  url: 'https://www.iurix.com/portfolio',
  sameAs: ['https://www.linkedin.com/in/iuridearaujo'],
  worksFor: { '@type': 'Organization', name: 'IURIX', url: 'https://www.iurix.com' },
  alumniOf: [
    { '@type': 'CollegeOrUniversity', name: 'SENAI CIMATEC' },
    { '@type': 'CollegeOrUniversity', name: 'Universidade de São Paulo' },
    { '@type': 'CollegeOrUniversity', name: 'Universidade Federal da Bahia' },
  ],
  knowsLanguage: ['Portuguese', 'English', 'Spanish', 'French'],
};

export default function Portfolio() {
  return (
    <>
      <SEO
        title='Portfolio — Iuri de Araujo'
        description='Lead Data Engineer & AI/ML Architect with 20+ years across cloud architecture, distributed data pipelines, and production ML/LLM systems for aviation, fintech, biometrics, and energy.'
        path='/portfolio'
        jsonLd={personSchema}
      />
      <main className='portfolio-page'>
        {/* Hero */}
        <header className='portfolio-hero'>
          <div className='portfolio-avatar' aria-hidden='true'>IS</div>
          <h1 className='portfolio-name'>Iuri de Araujo</h1>
          <div className='portfolio-roles' aria-label='Lead Data Engineer, AI and ML Architect, Cloud Systems Leader'>
            <span>Data & A.I. Engineer</span>
            <span>AI &amp; ML Architect</span>
            <span>Cloud Systems Leader</span>
          </div>
          <p className='portfolio-summary'>
            20+ years designing and delivering large-scale data architectures across AWS, GCP, and Azure —
            from mission-critical airline systems to biometric platforms, fintech pipelines, and production
            LLM/RAG applications. Hands-on technical leader who architects, codes, mentors, and translates
            deep technical work into outcomes executives can act on.
          </p>
          <div className='portfolio-contact'>
            <a href='mailto:contact@iurix.com'>contact@iurix.com</a>
            <a href='https://www.linkedin.com/in/iuridearaujo' target='_blank' rel='noopener noreferrer'>LinkedIn</a>
            <a href='https://www.iurix.com'>iurix.com</a>
          </div>
        </header>

        {/* Stats */}
        <div className='portfolio-stats'>
          <div className='portfolio-stat'>
            <div className='portfolio-stat-number'>20+</div>
            <div className='portfolio-stat-label'>Years Experience</div>
          </div>
          <div className='portfolio-stat'>
            <div className='portfolio-stat-number'>3</div>
            <div className='portfolio-stat-label'>Cloud Platforms</div>
          </div>
          <div className='portfolio-stat'>
            <div className='portfolio-stat-number'>10+</div>
            <div className='portfolio-stat-label'>Industries Served</div>
          </div>
          <div className='portfolio-stat'>
            <div className='portfolio-stat-number'>4</div>
            <div className='portfolio-stat-label'>Languages Spoken</div>
          </div>
        </div>

        {/* Skills */}
        <section className='portfolio-section'>
          <h2>Core Competencies</h2>
          <div className='portfolio-skills-grid'>
            {SKILL_GROUPS.map((group) => (
              <div key={group.title} className='portfolio-skill-group glass-dark'>
                <h3>{group.title}</h3>
                <div className='portfolio-tags'>
                  {group.skills.map((skill) => <span key={skill}>{skill}</span>)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recent work case study */}
        <section className='portfolio-section'>
          <h2>Recent Work</h2>
          <div className='portfolio-case-study'>
            <span className='case-study-tag'>2026 · Live Engagement</span>
            <h3>IURIX.com — SEO &amp; Platform Engineering Overhaul</h3>
            <p style={{ color: 'var(--gray-300)' }}>
              End-to-end technical SEO and engineering pass on this very site (a React/CRA single-page app),
              done in direct collaboration with Claude:
            </p>
            <ul style={{ color: 'var(--gray-300)', lineHeight: 1.8 }}>
              <li>Per-route metadata, canonical URLs, Open Graph/Twitter tags, and Organization/BlogPosting/Person JSON-LD via a shared SEO component.</li>
              <li>Technical crawlability: sitemap.xml, robots.txt, and an IndexNow integration wired into AWS CodePipeline/CodeBuild for near-instant Bing/Yandex re-indexing.</li>
              <li>Live financial data integration: a currency ticker and historical rate charts pulled directly from the European Central Bank's public XML feeds.</li>
              <li>Content architecture: replaced duplicated mock content across Blog/Jobs/Finance with real per-post URLs, structured job teasers, and live data.</li>
              <li>Dependency and toolchain conflict resolution across a decade-accreted CRA project (legacy PayPal/HTML-parser packages, React version alignment, TypeScript peer conflicts).</li>
              <li>Accessibility and semantic fixes: heading hierarchy, landmark regions, reduced-motion handling for video/animated backgrounds.</li>
            </ul>
            <div className='portfolio-proof-links'>
              <a href='/finances'>See it live: Finance →</a>
              <a href='/blog'>See it live: Blog →</a>
              <a href='/jobs'>See it live: Jobs →</a>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className='portfolio-section'>
          <h2>Experience</h2>
          <div className='portfolio-timeline'>
            {TIMELINE.map((item) => (
              <div className={`portfolio-timeline-item${item.concurrent ? ' is-concurrent' : ''}`} key={`${item.org}-${item.period}`}>
                <div className='portfolio-role-title'>{item.role} — {item.org}</div>
                <div className='portfolio-role-meta'>
                  {item.period} · {item.location}
                  {item.concurrent && <><br /><span className='concurrent-flag'>⚠ {item.concurrentNote}</span></>}
                </div>
                <ul>
                  {item.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Education */}
        <section className='portfolio-section'>
          <h2>Education</h2>
          <div className='portfolio-edu-grid'>
            {EDUCATION.map((e) => (
              <div key={e.degree} className='portfolio-edu-card glass-dark'>
                <h4>{e.degree}</h4>
                <p>{e.school}</p>
                <p>{e.period}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Certifications */}
        <section className='portfolio-section'>
          <h2>Certifications &amp; Memberships</h2>
          <div className='portfolio-badges'>
            {CERTIFICATIONS.map((c) => <span key={c}>{c}</span>)}
          </div>
        </section>

        {/* Languages */}
        <section className='portfolio-section'>
          <h2>Languages</h2>
          <div className='portfolio-languages'>
            {LANGUAGES.map((l) => (
              <div className='portfolio-language-row' key={l.name}>
                <span>{l.name}</span>
                <div className='portfolio-language-bar'>
                  <div style={{ width: `${l.pct}%` }} />
                </div>
                <span>{l.level}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className='portfolio-cta'>
          <h2>Let's build something.</h2>
          <p style={{ color: 'var(--gray-400)' }}>Open to consulting, leadership, and hands-on engineering engagements.</p>
          <div className='portfolio-cta-actions'>
            <a href='mailto:contact@iurix.com' className='btn btn--primary btn--large'>Email Me</a>
            <a href='https://www.linkedin.com/in/iuridearaujo' target='_blank' rel='noopener noreferrer' className='btn btn--outline btn--large'>Connect on LinkedIn</a>
          </div>
        </section>
      </main>
      <Copyrights />
      <Footer />
    </>
  );
}
